import { usePreview } from '.'
import { toast } from '@utils/functions/notificationToast'
import { generateExcelFileName } from '../helpers/generateExcelFileName'

import type { AxiosError } from 'axios'
import type { ExportPreviewDataFormProps } from './types'

import * as apiPreview from '@services/api/preview'
import type {
  ConsolidatedPreviewDetailsRequestProps,
  GetReturnPreviewQueryParamsProps,
  GetReturnPreviewResponseProps,
  SubmitReturnPreviewRequestProps
} from '@services/api/preview/types'
import { getAccountIdFromValue } from '@pages/Preview/components/modals/ReturnPreviousModal/constants/returnPreviewAccountOptions'

type BackendErrorResponse = {
  errorMessage?: string | string[]
  message?: string | string[]
}

const getBackendErrorMessage = (error: unknown, fallbackMessage: string) => {
  const data = (error as AxiosError<BackendErrorResponse>)?.response?.data
  const message = data?.errorMessage || data?.message

  if (Array.isArray(message)) {
    return message[0] || fallbackMessage
  }

  return message?.trim() || fallbackMessage
}

const exportConsolidatedPreview = async (formData: ExportPreviewDataFormProps) => {
  const { setIsDownloadPreviewLoading, setIsOpenConfirmExportPreview } = usePreview.getState()

  try {
    const response = await apiPreview.exportConsolidatedPreview({} as unknown as ConsolidatedPreviewDetailsRequestProps)

    const fileName = generateExcelFileName(formData.competenceDate)
    const url = window.URL.createObjectURL(new Blob([response]))
    const link = document.createElement('a')

    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)

    link.click()
    link.remove()

    toast.success('Exportação de Prévia realizada com sucesso!')
  } catch (error) {
    toast.error('Não foi possível exportar a Prévia. Tente novamente.', (error as AxiosError)?.status)
  } finally {
    setIsOpenConfirmExportPreview(false)
    setIsDownloadPreviewLoading(false)
  }
}

const adjustPreview = async (id: string) => {
  const { setIsAdjustPreviewLoading } = usePreview.getState()

  setIsAdjustPreviewLoading(true)

  try {
    await apiPreview.adjustPreview(id)

    toast.success('Justificativa de desvio aplicada com sucesso!')

    return true
  } catch (error) {
    toast.error('Falha ao aplicar justificativa de desvio!', (error as AxiosError)?.status)
    return false
  } finally {
    setIsAdjustPreviewLoading(false)
  }
}

const returnPreview = async (id: string) => {
  const { setIsReturnPreviewLoading } = usePreview.getState()

  setIsReturnPreviewLoading(true)

  try {
    await apiPreview.returnPreview(id)

    toast.success('Devolução da Prévia enviada com sucesso!')

    return true
  } catch (error) {
    const errorMessage = getBackendErrorMessage(
      error,
      'Não foi possível enviar a Devolução da Prévia. Tente novamente!'
    )
    toast.error(errorMessage, (error as AxiosError)?.status)
    return false
  } finally {
    setIsReturnPreviewLoading(false)
  }
}

/**
 * POST /api/previaapproval/return/submit
 * Envia a devolução de fato (rascunho salvo vira devolução efetiva).
 */
const submitReturnPreview = async (params: SubmitReturnPreviewRequestProps): Promise<boolean> => {
  const { setIsReturnPreviewLoading } = usePreview.getState()

  const { branchCode, weekNumber, referenceDate } = params
  if (!branchCode || referenceDate == null) {
    toast.error('Dados da prévia incompletos. Tente novamente.')
    return false
  }

  setIsReturnPreviewLoading(true)

  try {
    await apiPreview.postReturnSubmit({ branchCode, weekNumber, referenceDate })

    toast.success('Devolução da Prévia enviada com sucesso!')

    return true
  } catch (error) {
    const errorMessage = getBackendErrorMessage(
      error,
      'Não foi possível enviar a Devolução da Prévia. Tente novamente!'
    )
    toast.error(errorMessage, (error as AxiosError)?.response?.status)
    return false
  } finally {
    setIsReturnPreviewLoading(false)
  }
}

const getReturnPreviewDetails = async (id: string) => {
  const { setIsFetchingReturnPreviewData, returnPreviewDetails, setReturnPreviewDetails } = usePreview.getState()

  setIsFetchingReturnPreviewData(true)

  try {
    const data = await apiPreview.getReturnPreviewDetails(id)

    setReturnPreviewDetails({
      ...returnPreviewDetails,
      [id]: data
    })

    return true
  } catch (error) {
    // Silencioso se não encontrar dados (rascunho não existe ainda)
    return false
  } finally {
    setIsFetchingReturnPreviewData(false)
  }
}

/**
 * GET /api/previaapproval/return
 * Busca estado da devolução (rascunho e/ou enviada) por branch, semana e data de referência.
 * Chamado ao abrir o modal Devolver Prévia para preencher o formulário quando já existe rascunho.
 */
const getReturnPreview = async (
  params: GetReturnPreviewQueryParamsProps
): Promise<GetReturnPreviewResponseProps | null> => {
  const { setIsFetchingReturnPreviewData, setReturnPreviewData } = usePreview.getState()

  setIsFetchingReturnPreviewData(true)

  try {
    const data = await apiPreview.getReturnPreview(params)
    setReturnPreviewData(data)
    return data
  } catch (error) {
    setReturnPreviewData(null)
    return null
  } finally {
    setIsFetchingReturnPreviewData(false)
  }
}

const saveDraftReturnPreview = async (rowData: any, formData: any) => {
  const {
    setIsSavingDraftReturnPreview,
    periodSelectedData
  } = usePreview.getState()

  setIsSavingDraftReturnPreview(true)

  try {
    const branchCode = rowData?.profitCenter ?? ''
    const weekNumber = Number(rowData?.weekNumber) ?? 0
    const referenceDate = periodSelectedData?.competenceDate ?? ''

    if (!branchCode || !referenceDate) {
      toast.error('Dados da prévia incompletos. Tente novamente.')
      return false
    }

    const accounts = formData?.accounts ?? []
    if (accounts.length === 0) {
      toast.error('Adicione ao menos uma conta e justificativa.')
      return false
    }

    for (const item of accounts) {
      const accountId = getAccountIdFromValue(item?.account)
      if (accountId == null) {
        toast.error(`Conta inválida: ${item?.account}. Selecione uma opção da lista.`)
        return false
      }
      const justification = item?.returnJustification?.trim() ?? ''
      const draftItemId = typeof item?.draftItemId === 'string' ? item.draftItemId.trim() : ''

      if (draftItemId) {
        await apiPreview.putReturnDraft(draftItemId, { accountId, justification })
      } else {
        await apiPreview.postReturnDraft({
          branchCode,
          weekNumber,
          referenceDate,
          accountId,
          justification
        })
      }
    }

    toast.success('Devolução da Prévia registrada como rascunho!')
    return true
  } catch (error) {
    const errorMessage = getBackendErrorMessage(
      error,
      'Não foi possível registrar a Devolução da Prévia. Tente novamente!'
    )
    toast.error(errorMessage, (error as AxiosError)?.response?.status)
    return false
  } finally {
    setIsSavingDraftReturnPreview(false)
  }
}

/**
 * DELETE /api/previaapproval/return/draft/{id}
 * Exclui rascunho(s) da devolução. Se houver mais de um item, chama a API para cada id.
 */
const deleteDraftReturnPreview = async (ids: string[]): Promise<boolean> => {
  const { setIsDeletingDraftReturnPreview, setReturnPreviewData } = usePreview.getState()

  if (!ids?.length) return false

  setIsDeletingDraftReturnPreview(true)

  try {
    for (const id of ids) {
      await apiPreview.deleteReturnDraft(String(id))
    }
    setReturnPreviewData(null)
    toast.success('Rascunho da devolução excluído com sucesso!')
    return true
  } catch (error) {
    const errorMessage = getBackendErrorMessage(error, 'Não foi possível excluir o rascunho. Tente novamente!')
    toast.error(errorMessage, (error as AxiosError)?.response?.status)
    return false
  } finally {
    setIsDeletingDraftReturnPreview(false)
  }
}

/**
 * RF09: Busca tipos de desvio específicos por conta e polaridade.
 * GET /api/deviationjustification/deviation-types
 *
 * São duas chamadas: uma para o "Tipo de Desvio" do bloco vs Budget e outra para o do bloco
 * vs Prévia S-1, cada uma com o `isPositive` da polaridade do seu próprio desvio.
 */
const getDeviationTypes = async (accountId: number, budgetIsPositive: boolean, s1IsPositive: boolean) => {
  const { setIsDeviationTypesLoading, setDeviationTypesBudget, setDeviationTypesS1 } = usePreview.getState()

  setIsDeviationTypesLoading(true)

  try {
    const [budgetTypes, s1Types] = await Promise.all([
      apiPreview.getDeviationTypes({ accountId, isPositive: budgetIsPositive }),
      apiPreview.getDeviationTypes({ accountId, isPositive: s1IsPositive })
    ])

    setDeviationTypesBudget(budgetTypes)
    setDeviationTypesS1(s1Types)
  } catch (error) {
    toast.error('Erro ao buscar tipos de desvio!', (error as AxiosError)?.status)
    setDeviationTypesBudget([])
    setDeviationTypesS1([])
  } finally {
    setIsDeviationTypesLoading(false)
  }
}

export {
  exportConsolidatedPreview,
  returnPreview,
  submitReturnPreview,
  getReturnPreviewDetails,
  getReturnPreview,
  saveDraftReturnPreview,
  deleteDraftReturnPreview,
  adjustPreview,
  getDeviationTypes
}
